/**
 * @tago/legal — single source of truth for Tago's legal documents.
 *
 * Both apps/marketing (tago.rent) and apps/mobile (app.tago.rent) consume
 * this package. The Spanish text is counsel-reviewed and legally
 * authoritative; English is an accurate translation per the Spanish docs'
 * own §16.6 / §12.5 controlling-language clauses.
 *
 * Content layout: src/content.ts holds the structured `LegalContent`
 * objects (intro + sections array). Renderers can either consume the
 * structured form directly or call `toMarkdown(content)` to get a
 * plain-text markdown string suitable for any markdown renderer
 * (react-markdown on web, react-native-markdown-display on RN).
 *
 * To revise a doc: edit src/content.ts, bump the effectiveDate string
 * inside the affected `LegalContent.effectiveDate`, deploy. Both
 * surfaces pick up the change on the next build — no per-app updates.
 */

export {
  getLegalContent,
  getLegalContentVersion,
  type LegalSlug,
  type LegalContent,
} from './src/content'
export { proposedRental20 } from './src/proposed-rental-2'
import type { LegalContent } from './src/content'

export const LEGAL_SLUGS = [
  'terms-of-service',
  'privacy-policy',
  'cancellation-policy',
] as const

export type LegalLocale = 'es' | 'en'

/**
 * Convert a structured LegalContent into a Markdown string.
 *
 * - Sections become `## H2` blocks.
 * - The intro is the first paragraph.
 * - The bullet character `•` (used in the source) is normalized to `-`
 *   so any markdown renderer treats them as proper list items.
 * - Hard newlines (`\n`) inside section bodies are preserved as
 *   markdown soft breaks; double newlines (`\n\n`) become paragraph
 *   breaks.
 */
export function toMarkdown(content: LegalContent): string {
  const lines: string[] = []

  // Intro — first paragraph, no heading.
  lines.push(normalizeBullets(content.intro))
  lines.push('')

  for (const section of content.sections) {
    lines.push(`## ${section.title}`)
    lines.push('')
    lines.push(normalizeBullets(section.body))
    lines.push('')
  }

  return lines.join('\n').trim()
}

function normalizeBullets(text: string): string {
  // Convert "• item" or "•  item" at line start to "- item" (markdown bullet).
  return text.replace(/^•\s+/gm, '- ')
}
